import { useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getJobServiceSelection, getService, putService } from 'queries';

import { Toast, parseErrors } from 'helpers';
import { JsonDataItem } from 'types';

type QueryKey = [group: string, id: string];

function useJob(id?: string) {
  const {
    data: job,
    refetch: refetchService,
    isLoading: isJobLoading,
  } = useQuery(['check-service-details', id] as QueryKey, getService, {
    enabled: !!id,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { data: serviceSelection, refetch: refetchServiceSelection } = useQuery(
    ['check-selected-services', id] as QueryKey,
    getJobServiceSelection,
    {
      enabled: !!id,
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );

  const {
    isSuccess: isSlotConfirmed,
    mutate: mutateRescheduleJob,
    isLoading: loadingAppointment,
    reset: resetReschedule,
  } = useMutation(putService, {
    onSuccess: () => {
      refetchService();
      refetchServiceSelection();
      Toast.show({
        description: 'Rescheduled successfully!',
        type: Toast.Type.Success,
      });
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const rescheduleJob = useCallback(
    (opts: Omit<Parameters<typeof putService>[0], 'jobId'>) => {
      id && mutateRescheduleJob({ ...opts, jobId: id });
    },
    [id, mutateRescheduleJob],
  );

  return {
    job: job || null,
    serviceSelection,
    rescheduleJob,
    isLoading: loadingAppointment || isJobLoading,
    isSlotConfirmed,
    resetReschedule,
    refetchService,
  };
}

export { useJob };
